<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headersToRender"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>
                    <th v-if="!readOnly" scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in paginatedData" :key="item.id" :class="rowClass(item)">
                    <td>
                        <div class="vendor-part-cell">
                            <PartNumberDisplay
                                :part-number="item.part_number"
                                :selected-part="part_selected"
                            />
                            <a
                                v-if="item.url"
                                :href="item.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="vendor-part-link"
                                title="Open vendor page"
                                @click.stop
                            >
                                <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" />
                            </a>
                        </div>
                    </td>
                    <td>{{ getVendorLabel(item) }}</td>
                    <td>
                        <span v-html="getDisplayedDescription(item, index)"></span>&nbsp;
                        <a
                            href="javascript:;"
                            v-if="shouldShowToggle(item.description)"
                            class="toggle-description"
                            @click="toggleRow(index)"
                        >
                            {{ isExpanded(index) ? 'show less' : 'show more' }}
                        </a>
                    </td>
                    <td>
                        <div class="vendor-updated-cell">
                            <span>{{ formatDateToUS(item.time_updated) }}</span>
                            <DateAgeBadge :date="item.time_updated" />
                        </div>
                    </td>
                    <td v-if="!listPriceOnly">
                        <span v-if="getYourPriceMeta(item).value != null">
                            {{
                                formatCurrencyAmount(
                                    getYourPriceMeta(item).value,
                                    getYourPriceMeta(item).currency
                                )
                            }}
                        </span>
                    </td>
                    <td>
                        <span v-if="getListPriceMeta(item).value != null">
                            {{
                                formatCurrencyAmount(
                                    getListPriceMeta(item).value,
                                    getListPriceMeta(item).currency
                                )
                            }}
                        </span>
                    </td>
                    <td>{{ item.uom }}</td>
                    <td>
                        <span v-if="Array.isArray(getSubstituteBadges(item))">
                            <span
                                v-for="(part, i) in getSubstituteBadges(item)"
                                :key="i"
                                class="badge bg-secondary me-1 dashboard-substitute-badge"
                            >
                                {{ part }}
                            </span>
                        </span>
                    </td>

                    <td>{{ item.manufacturer_name }}</td>
                    <td>{{ item.qty_minimum }}</td>
                    <td>{{ getDisplayQty(item) }}</td>
                    <td v-if="!readOnly">
                        <a
                            href="javascript:;"
                            @click="triggerMoreDetails(item)"
                            class="mx-2 dashboard-action-link"
                            title="More details"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" />
                        </a>
                        <a
                            href="javascript:;"
                            @click="toggleQuoteModal(item)"
                            title="Add Quote"
                            class="dashboard-action-link"
                        >
                            <font-awesome-icon :icon="['fas', 'plus']" />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage <= 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage >= totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatDateToUS } from '@/helpers/dates.js'
    import { formatNumeric, normalizePart, parseNumeric } from '@/helpers/string.js'
    import {
        buildDashboardRowHighlightClass,
        compareHighlightedCustomerFirst,
        compareSelectedPartFirst,
        getDashboardRowCustomerName,
    } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'
    import {
        firstTier,
        formatCurrencyAmount,
        getQuoteListPrice,
        getQuoteUnitCost,
        getListPriceMeta,
        getYourPriceMeta,
        getDisplayQty,
        getVendorForQuote,
        getVendorLabel,
    } from '@/utils/vendorRow'
    import DateAgeBadge from '@/components/shared/DateAgeBadge.vue'

    export default {
        name: 'VendorTable',
        components: {
            DateAgeBadge,
            PartNumberDisplay,
        },
        props: {
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            data: {
                expanded: false,
                type: Array,
                default: () => [],
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            listPriceOnly: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                headers: [
                    { key: 'part_number', label: 'Part Number', type: 'string' },
                    { key: 'ui_vendor_name', label: 'Vendor', type: 'string' },
                    { key: 'description', label: 'Description', type: 'string' },
                    { key: 'time_updated', label: 'Updated', type: 'date' },
                    { key: 'your_price', label: 'Your Price', type: 'numeric' },
                    { key: 'list_price', label: 'List Price', type: 'numeric' },
                    { key: 'uom', label: 'UOM', type: 'string' },
                    { key: 'substitute_for', label: 'Substitute for', type: 'string' },
                    { key: 'manufacturer_name', label: 'Manufacturer', type: 'string' },
                    { key: 'qty_minimum', label: 'Min Qty', type: 'numeric' },
                    { key: 'display_qty', label: 'Stock', type: 'numeric' },
                ],
                columnType: 'string',
                sortColumn: 0,
                sortDirection: 'asc',
                currentPage: 1,
                itemsPerPage: 10,
                expandedRows: new Set(),
                maxLength: 90,
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal']),
            headersToRender() {
                return this.headers.filter(
                    (header) => !(this.listPriceOnly && header.key === 'your_price')
                )
            },
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const partPriority = compareSelectedPartFirst(
                        a?.part_number,
                        b?.part_number,
                        this.part_selected
                    )
                    if (partPriority !== 0) return partPriority

                    const customerPriority = compareHighlightedCustomerFirst(
                        getDashboardRowCustomerName(a),
                        getDashboardRowCustomerName(b),
                        this.highlightedCustomer
                    )
                    if (customerPriority !== 0) return customerPriority

                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = this.getSortValue(a, key)
                    let valueB = this.getSortValue(b, key)

                    if (this.columnType === 'numeric') {
                        valueA = parseNumeric(valueA, 0)
                        valueB = parseNumeric(valueB, 0)
                    } else if (this.columnType === 'date') {
                        valueA = new Date(valueA)
                        valueB = new Date(valueB)
                    } else {
                        valueA = String(valueA).toLowerCase()
                        valueB = String(valueB).toLowerCase()
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.data.length / this.itemsPerPage))
            },
        },
        watch: {
            part_selected() {
                this.currentPage = 1
            },
        },
        methods: {
            formatNumeric,
            formatDateToUS,
            formatCurrencyAmount,
            firstTier,
            getListPriceMeta,
            getYourPriceMeta,
            getVendorLabel,
            getDisplayQty,
            stripHtmlAndKeepSpaces(content) {
                if (!content) {
                    return ''
                }

                const withLineBreaks = content.replace(/<br\s*\/?>/gi, '\n')
                const textOnly = withLineBreaks.replace(/<[^>]+>/g, ' ')
                const cleannedString = textOnly
                    .replace(/[ \t]+/g, ' ')
                    .replace(/\n\s*/g, '\n')
                    .trim()

                return cleannedString.slice(0, this.maxLength) + '...'
            },
            hasHTML(content) {
                const htmlTagRegex = /<[^>]+>/
                return htmlTagRegex.test(content)
            },
            isExpanded(index) {
                return this.expandedRows.has(index)
            },
            toggleRow(index) {
                if (this.isExpanded(index)) {
                    this.expandedRows.delete(index)
                } else {
                    this.expandedRows.add(index)
                }
            },
            shouldShowToggle(content) {
                return content && content.length > this.maxLength
            },
            getDisplayedDescription(row, index) {
                const content = row.description || ''
                if (this.isExpanded(index) || content.length <= this.maxLength) {
                    return content
                }
                if (this.hasHTML(content)) {
                    return this.stripHtmlAndKeepSpaces(content)
                }
                return content.slice(0, this.maxLength) + '...'
            },
            triggerMoreDetails(details) {
                this.$emit('more-details', details)
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                return this.headersToRender[index]?.key
            },
            getSortValue(row, key) {
                if (key === 'your_price') {
                    return this.getYourPriceMeta(row).value ?? 0
                }
                if (key === 'list_price') {
                    return this.getListPriceMeta(row).value ?? 0
                }
                if (key === 'display_qty') {
                    return this.getDisplayQty(row)
                }
                return row?.[key]
            },
            getSubstituteBadges(item) {
                const raw = item?.substitute_parts ?? item?.substitute_for ?? null
                if (!raw && item?.part_number_manufacturer) {
                    return [item.part_number_manufacturer]
                }
                if (Array.isArray(raw)) {
                    return raw.map((entry) => this.formatSubstituteEntry(entry)).filter(Boolean)
                }
                if (this.isPlainObject(raw)) {
                    const formatted = this.formatSubstituteEntry(raw)
                    return formatted ? [formatted] : []
                }
                if (raw) {
                    return [String(raw)]
                }
                return []
            },
            formatSubstituteEntry(entry) {
                if (entry == null) {
                    return null
                }
                if (typeof entry === 'string') {
                    const parsed = this.tryParseJson(entry)
                    if (this.isPlainObject(parsed)) {
                        if (parsed.part_number) {
                            return parsed.part_number
                        }
                        if (parsed.substitute_for) {
                            return parsed.substitute_for
                        }
                    }
                    return entry
                }
                if (this.isPlainObject(entry)) {
                    if (entry.part_number) {
                        return entry.part_number
                    }
                    if (entry.substitute_for) {
                        return entry.substitute_for
                    }
                    try {
                        return JSON.stringify(entry)
                    } catch (e) {
                        return String(entry)
                    }
                }
                return String(entry)
            },
            isPlainObject(value) {
                return value !== null && typeof value === 'object' && !Array.isArray(value)
            },
            tryParseJson(value) {
                if (typeof value !== 'string') {
                    return null
                }
                try {
                    return JSON.parse(value)
                } catch (e) {
                    return null
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            toggleQuoteModal(item) {
                const mappedVendorForQuote = getVendorForQuote(item)
                this.$emit('toggleQuoteModal', {
                    ...item,
                    part_quoted: item?.part_number || '',
                    vendor: mappedVendorForQuote,
                    ui_vendor_name: mappedVendorForQuote,
                    unit_cost: getQuoteUnitCost(item),
                    list_price: getQuoteListPrice(item),
                    description: item?.description || '',
                })
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    customerName: getDashboardRowCustomerName(item),
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                    activePart: this.isActivePart(item.part_number),
                })
            },
        },
    }
</script>

<style scoped>
    .toggle-description {
        color: var(--color-brand-primary);
        cursor: pointer;
        font-weight: 600;
        text-decoration: none;
    }

    .vendor-part-cell {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
        justify-content: center;
    }

    .vendor-part-link {
        align-items: center;
        color: var(--color-brand-primary);
        display: inline-flex;
        font-size: 0.8rem;
        line-height: 1;
        text-decoration: none;
    }

    .vendor-part-link:hover,
    .vendor-part-link:focus {
        color: var(--color-brand-primary-strong);
    }

    .vendor-updated-cell {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        min-width: 6.5rem;
    }

    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .badge-danger {
        color: var(--color-text-inverse);
        background-color: var(--color-brand-primary) !important;
    }

    .badge {
        display: inline-block;
        padding: 0.25em 0.4em;
        font-size: 75%;
        font-weight: 700;
        line-height: 1;
        text-align: center;
        white-space: nowrap;
        vertical-align: baseline;
        border-radius: 0.25rem;
    }
</style>
